import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { LOGO_URL } from "../../lib/api";
import { LayoutDashboard, Calendar, Clock, Sparkles, Users, Image, Star, Settings, LogOut, Loader2, Menu, X } from "lucide-react";
import { useState } from "react";
import NotificationBell from "./NotificationBell";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/agenda", label: "Agenda", icon: Calendar, testid: "admin-nav-calendar" },
  { to: "/admin/beschikbaarheid", label: "Beschikbaarheid", icon: Clock, testid: "admin-nav-availability" },
  { to: "/admin/behandelingen", label: "Behandelingen", icon: Sparkles, testid: "admin-nav-treatments" },
  { to: "/admin/klanten", label: "Klanten", icon: Users, testid: "admin-nav-customers" },
  { to: "/admin/galerij", label: "Fotogalerij", icon: Image, testid: "admin-nav-gallery" },
  { to: "/admin/reviews", label: "Reviews", icon: Star, testid: "admin-nav-reviews" },
  { to: "/admin/instellingen", label: "Instellingen", icon: Settings, testid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, checking, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-brand-neutral"><Loader2 className="animate-spin text-brand-pink" size={32} /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-6 h-20 border-b border-brand-border">
        <img src={LOGO_URL} alt="Nails Renss" className="h-10 w-10 rounded-full" />
        <div>
          <p className="font-display text-lg font-semibold text-brand-charcoal leading-none">Nails Renss</p>
          <p className="text-xs text-brand-muted mt-0.5">Beheer</p>
        </div>
        <div className="ml-auto hidden md:block"><NotificationBell /></div>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={n.testid}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-brand-charcoal text-white" : "text-brand-charcoal/75 hover:bg-brand-blush"}`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-brand-border">
        <button onClick={doLogout} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-brand-muted hover:bg-brand-blush w-full" data-testid="admin-logout-button">
          <LogOut size={18} /> Uitloggen
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-brand-neutral flex">
      <aside className="hidden md:flex w-64 bg-white border-r border-brand-border flex-col fixed inset-y-0">
        <SidebarContent />
      </aside>

      {/* mobile header */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-brand-border h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Nails Renss" className="h-9 w-9 rounded-full" />
          <span className="font-display text-lg font-semibold">Beheer</span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <button onClick={() => setOpen(!open)} className="p-2" data-testid="admin-mobile-toggle">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)}>
          <aside className="w-64 bg-white h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            <SidebarContent />
          </aside>
        </div>
      )}

      <main className="flex-1 md:ml-64 pt-20 md:pt-0 min-w-0">
        <div className="p-5 md:p-8 max-w-6xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
